import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  radiosCercanas,
  eventosCercanos,
  locutoresCercanos,
  ubicacionesDetectables,
  opcionesDistancia,
  opcionesIdioma,
  opcionesCategoria,
  reglasRadiosCercanas,
  statsRadiosCercanas,
  formatearNumero,
  getRadiosCercanasConFiltros,
  getEventosCercanosConFiltros,
  getLocutoresCercanosConFiltros,
  type RadioCercana,
  type EventoCercano,
  type LocutorCercano,
} from '@/mocks/radiosCercanas';

function DistanciaBadge({ km }: { km: number }) {
  const color = km <= 2 ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
    km <= 5 ? 'bg-amber-100 text-amber-700 border-amber-200' :
    'bg-background-100 text-foreground-500 border-background-200';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${color}`}>
      <i className="ri-map-pin-line text-[8px]"></i>
      {km} km
    </span>
  );
}

function LiveBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/90 text-white text-[9px] font-bold">
      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
      EN VIVO
    </span>
  );
}

function TendenciaBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/90 text-white text-[9px] font-bold">
      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
      TENDENCIA
    </span>
  );
}

// ─── Radio Card ───
function RadioCercanaCardFull({ radio, onClick }: { radio: RadioCercana; onClick: () => void }) {
  const cat = opcionesCategoria.find(c => c.id === radio.categoria);

  return (
    <div
      onClick={onClick}
      className="group block rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-emerald-300/60 transition-all duration-300 cursor-pointer"
    >
      <div className="relative h-40 overflow-hidden">
        <img src={radio.imagenUrl} alt={radio.nombre} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/95 via-background-50/30 to-transparent"></div>
        <div className="absolute top-3 left-3 flex items-center gap-2 flex-wrap">
          {radio.estaEnVivo && <LiveBadge />}
          {radio.enTendencia && <TendenciaBadge />}
          <DistanciaBadge km={radio.distancia} />
        </div>
        <div className="absolute top-3 right-3">
          <img src={radio.logoUrl} alt="" className="w-10 h-10 rounded-xl object-cover border-2 border-white/80 shadow-sm" />
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-sm font-bold text-foreground-950 line-clamp-1">{radio.nombre}</h3>
          {cat && (
            <span className="shrink-0 w-6 h-6 rounded-full bg-background-100 flex items-center justify-center">
              <i className={`${cat.icono} text-[10px] text-foreground-400`}></i>
            </span>
          )}
        </div>
        <p className="text-[10px] text-foreground-500 mb-1.5">{radio.frecuencia} · {radio.ciudad}, {radio.estado} · {radio.pais}</p>
        <div className="flex items-center gap-3 text-[9px] text-foreground-400 mb-2">
          <span className="flex items-center gap-1"><i className="ri-headphone-line text-[8px]"></i>{formatearNumero(radio.oyentes)}</span>
          <span className="flex items-center gap-1"><i className="ri-star-fill text-[8px] text-amber-500"></i>{radio.rating}</span>
          <span className="text-foreground-500 font-medium truncate flex items-center gap-1">
            <img src={radio.locutorAvatarUrl} alt="" className="w-4 h-4 rounded-full object-cover" />
            {radio.locutorPrincipal}
          </span>
        </div>
        <div className="flex flex-wrap gap-1">
          {radio.tags.slice(0, 3).map(tag => (
            <span key={tag} className="px-1.5 py-0.5 rounded-md bg-background-100 text-[8px] text-foreground-500 border border-background-200/50">{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Evento Card ───
function EventoCercanoCardFull({ evento, onClick }: { evento: EventoCercano; onClick: () => void }) {
  const fecha = new Date(evento.fecha);
  const fechaStr = fecha.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <div
      onClick={onClick}
      className="group block rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-violet-300/60 transition-all duration-300 cursor-pointer"
    >
      <div className="relative h-36 overflow-hidden">
        <img src={evento.imagenUrl} alt={evento.titulo} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/95 via-background-50/30 to-transparent"></div>
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="px-2 py-0.5 rounded-full bg-violet-500/90 text-white text-[9px] font-bold">{evento.tipo}</span>
          <DistanciaBadge km={evento.distancia} />
        </div>
        {evento.esGratuito && (
          <div className="absolute top-3 right-3">
            <span className="px-2 py-0.5 rounded-full bg-emerald-500/90 text-white text-[9px] font-bold">GRATIS</span>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-sm font-bold text-foreground-950 line-clamp-1 mb-1">{evento.titulo}</h3>
        <p className="text-[10px] text-foreground-500 mb-1.5">
          <img src={evento.radioLogoUrl} alt="" className="w-4 h-4 rounded-full object-cover inline mr-1 align-middle" />
          {evento.radioNombre}
        </p>
        <div className="flex items-center gap-3 text-[9px] text-foreground-400">
          <span className="flex items-center gap-1"><i className="ri-calendar-line text-[8px]"></i>{fechaStr}</span>
          <span>{evento.hora}</span>
          <span className="flex items-center gap-1"><i className="ri-group-line text-[8px]"></i>{formatearNumero(evento.participantes)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Locutor Card ───
function LocutorCercanoCardFull({ locutor, onClick }: { locutor: LocutorCercano; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className="group flex items-center gap-4 p-4 rounded-2xl bg-background-50 border border-background-200/70 hover:border-amber-300/60 transition-all duration-300 cursor-pointer"
    >
      <div className="relative shrink-0">
        <img src={locutor.avatarUrl} alt={locutor.nombre} className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-sm" />
        {locutor.estaEnVivo && (
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 border-2 border-white flex items-center justify-center">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-bold text-foreground-950 line-clamp-1">{locutor.nombre}</h3>
        <p className="text-[10px] text-foreground-500 mb-1">
          <img src={locutor.radioLogoUrl} alt="" className="w-3.5 h-3.5 rounded-full object-cover inline mr-1 align-middle" />
          {locutor.radioNombre} · {locutor.radioFrecuencia}
        </p>
        <div className="flex items-center gap-3 text-[9px] text-foreground-400">
          <DistanciaBadge km={locutor.distancia} />
          <span className="flex items-center gap-1"><i className="ri-group-line text-[8px]"></i>{formatearNumero(locutor.seguidores)}</span>
          <span className="text-foreground-500">{locutor.especialidad}</span>
        </div>
      </div>
      <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-primary-500 transition-colors"></i>
    </div>
  );
}

// ─── Modal de Detalle ───
function RadioDetailModal({ radio, onClose }: { radio: RadioCercana; onClose: () => void }) {
  if (!radio) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-background-50 rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-background-200/70" onClick={e => e.stopPropagation()}>
        <div className="relative h-48 overflow-hidden rounded-t-3xl">
          <img src={radio.imagenUrl} alt={radio.nombre} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent"></div>
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-foreground-950/50 backdrop-blur-sm text-white flex items-center justify-center hover:bg-foreground-950/70 transition-all cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
          <div className="absolute top-4 left-4 flex items-center gap-2">
            {radio.estaEnVivo && <LiveBadge />}
            {radio.enTendencia && <TendenciaBadge />}
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-start gap-4 mb-4">
            <img src={radio.logoUrl} alt="" className="w-16 h-16 rounded-2xl object-cover border-2 border-background-200 shadow-sm shrink-0" />
            <div>
              <h2 className="text-xl font-bold text-foreground-950">{radio.nombre}</h2>
              <p className="text-sm text-foreground-500">{radio.frecuencia} · {radio.ciudad}, {radio.estado}</p>
              <DistanciaBadge km={radio.distancia} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            {[
              { label: 'Oyentes', value: formatearNumero(radio.oyentes), icon: 'ri-headphone-line' },
              { label: 'Rating', value: radio.rating.toString(), icon: 'ri-star-fill' },
              { label: 'Categoría', value: radio.categoria, icon: 'ri-price-tag-3-line' },
            ].map((s, i) => (
              <div key={i} className="p-3 rounded-xl bg-background-100 text-center">
                <i className={`${s.icon} text-sm text-foreground-400 mb-1 block`}></i>
                <p className="text-base font-bold text-foreground-950">{s.value}</p>
                <p className="text-[9px] text-foreground-400">{s.label}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-foreground-600 leading-relaxed mb-4">{radio.descripcion}</p>

          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-[10px] text-foreground-500">
              <i className="ri-user-voice-line text-foreground-400"></i>
              <span className="font-medium text-foreground-950">Locutor:</span>
              <img src={radio.locutorAvatarUrl} alt="" className="w-5 h-5 rounded-full object-cover" />
              {radio.locutorPrincipal}
            </div>
            <div className="flex items-center gap-2 text-[10px] text-foreground-500">
              <i className="ri-translate text-foreground-400"></i>
              <span className="font-medium text-foreground-950">Idioma:</span>
              {radio.idioma === 'es' ? 'Español' : radio.idioma}
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {radio.tags.map(tag => (
              <span key={tag} className="px-2 py-1 rounded-lg bg-background-100 text-[10px] text-foreground-500 border border-background-200/50">{tag}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function EventoDetailModal({ evento, onClose }: { evento: EventoCercano; onClose: () => void }) {
  if (!evento) return null;
  const fecha = new Date(evento.fecha);
  const fechaStr = fecha.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-background-50 rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-background-200/70" onClick={e => e.stopPropagation()}>
        <div className="relative h-48 overflow-hidden rounded-t-3xl">
          <img src={evento.imagenUrl} alt={evento.titulo} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent"></div>
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-foreground-950/50 backdrop-blur-sm text-white flex items-center justify-center hover:bg-foreground-950/70 transition-all cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className="px-2 py-0.5 rounded-full bg-violet-500/90 text-white text-[9px] font-bold">{evento.tipo}</span>
            {evento.esGratuito && <span className="px-2 py-0.5 rounded-full bg-emerald-500/90 text-white text-[9px] font-bold">GRATIS</span>}
          </div>
        </div>
        <div className="p-6">
          <h2 className="text-xl font-bold text-foreground-950 mb-2">{evento.titulo}</h2>
          <p className="text-sm text-foreground-500 mb-4">
            <img src={evento.radioLogoUrl} alt="" className="w-5 h-5 rounded-full object-cover inline mr-1.5 align-middle" />
            {evento.radioNombre}
          </p>

          <div className="grid grid-cols-2 gap-3 mb-4">
            {[
              { label: 'Fecha', value: fechaStr, icon: 'ri-calendar-line' },
              { label: 'Hora', value: evento.hora, icon: 'ri-time-line' },
              { label: 'Distancia', value: `${evento.distancia} km`, icon: 'ri-map-pin-line' },
              { label: 'Participantes', value: formatearNumero(evento.participantes), icon: 'ri-group-line' },
            ].map((s, i) => (
              <div key={i} className="p-3 rounded-xl bg-background-100 text-center">
                <i className={`${s.icon} text-sm text-foreground-400 mb-1 block`}></i>
                <p className="text-sm font-bold text-foreground-950">{s.value}</p>
                <p className="text-[9px] text-foreground-400">{s.label}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-foreground-600 leading-relaxed mb-4">{evento.descripcion}</p>

          <div className="flex flex-wrap gap-1.5">
            {evento.tags.map(tag => (
              <span key={tag} className="px-2 py-1 rounded-lg bg-background-100 text-[10px] text-foreground-500 border border-background-200/50">{tag}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Página Principal ───
export default function RadiosCercanasPage() {
  const navigate = useNavigate();
  const [ubicacionIdx, setUbicacionIdx] = useState(0);
  const [activeTab, setActiveTab] = useState<'radios' | 'eventos' | 'locutores' | 'reglas'>('radios');
  const [activeView, setActiveView] = useState<'contenido' | 'reglas'>('contenido');
  const [distanciaFilter, setDistanciaFilter] = useState('todas');
  const [idiomaFilter, setIdiomaFilter] = useState('todos');
  const [categoriaFilter, setCategoriaFilter] = useState('todas');
  const [selectedRadio, setSelectedRadio] = useState<RadioCercana | null>(null);
  const [selectedEvento, setSelectedEvento] = useState<EventoCercano | null>(null);

  const ubicacion = ubicacionesDetectables[ubicacionIdx];
  const distOpt = opcionesDistancia.find(d => d.id === distanciaFilter);

  const radiosFiltradas = getRadiosCercanasConFiltros(
    ubicacion.paisCodigo, ubicacion.ciudad,
    distOpt?.maxKm, idiomaFilter, categoriaFilter
  );
  const eventosFiltrados = getEventosCercanosConFiltros(ubicacion.paisCodigo, ubicacion.ciudad, distOpt?.maxKm);
  const locutoresFiltrados = getLocutoresCercanosConFiltros(ubicacion.paisCodigo, ubicacion.ciudad, distOpt?.maxKm, idiomaFilter);

  const tabs = [
    { id: 'radios' as const, label: 'Radios Cerca', icon: 'ri-radio-line', count: radiosFiltradas.length },
    { id: 'eventos' as const, label: 'Eventos Cerca', icon: 'ri-calendar-event-line', count: eventosFiltrados.length },
    { id: 'locutores' as const, label: 'Locutores Cerca', icon: 'ri-user-voice-line', count: locutoresFiltrados.length },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="relative h-[380px] md:h-[440px] w-full">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cozy%20neighborhood%20scene%20with%20radio%20towers%20and%20city%20skyline%20in%20distance%2C%20golden%20sunset%20light%20over%20residential%20area%2C%20stylized%20illustration%20with%20geometric%20shapes%2C%20local%20community%20aesthetic%2C%20warm%20earthy%20green%20and%20amber%20tones%2C%20artistic%20editorial%20composition&width=1600&height=600&seq=rc-hero-bg&orientation=landscape"
            alt="Radios Cercanas"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-semibold mb-4 border border-white/30">
            <i className="ri-map-pin-line"></i>
            DESCUBRÍ LO LOCAL
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 drop-shadow-lg">
            Radios <span className="text-emerald-300">Cerca de Ti</span>
          </h1>
          <p className="text-white/80 max-w-xl text-sm md:text-base mb-6">
            Descubrí radios, eventos y locutores que están físicamente cerca. La radio local es la que mejor conoce tu comunidad.
          </p>

          {/* Location selector in hero */}
          <div className="inline-flex items-center gap-3 bg-white/15 backdrop-blur-md rounded-full px-2 py-2 border border-white/20">
            <span className="pl-3 text-white/70 text-sm"><i className="ri-map-pin-line"></i></span>
            <select
              value={ubicacionIdx}
              onChange={(e) => setUbicacionIdx(Number(e.target.value))}
              className="bg-transparent text-sm font-semibold text-white px-2 py-1.5 rounded-full outline-none cursor-pointer appearance-none pr-8"
              style={{ backgroundImage: 'none' }}
            >
              {ubicacionesDetectables.map((u, i) => (
                <option key={i} value={i} className="text-foreground-950">{u.ciudad}, {u.estado} — {u.pais}</option>
              ))}
            </select>
            <span className="pr-2 text-white/60 text-sm"><i className="ri-arrow-down-s-line"></i></span>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <div className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-xl border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-foreground-400 hover:text-foreground-950 transition-colors">
              <i className="ri-arrow-left-line"></i>
            </Link>
            <span className="text-sm font-bold text-foreground-950">
              {ubicacion.ciudad}, {ubicacion.pais}
            </span>
          </div>
          <div className="flex items-center gap-4 text-[10px] text-foreground-500">
            <span className="flex items-center gap-1"><i className="ri-radio-line"></i>{statsRadiosCercanas.totalRadios} radios</span>
            <span className="flex items-center gap-1"><i className="ri-calendar-event-line"></i>{statsRadiosCercanas.totalEventos} eventos</span>
            <span className="flex items-center gap-1"><i className="ri-user-voice-line"></i>{statsRadiosCercanas.totalLocutores} locutores</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveView(activeView === 'contenido' ? 'reglas' : 'contenido')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                activeView === 'reglas' ? 'bg-primary-500 text-white border-primary-500' : 'bg-background-100 text-foreground-600 border-background-200'
              }`}
            >
              <i className={activeView === 'reglas' ? 'ri-scales-line' : 'ri-list-check-2'}></i>
              {activeView === 'reglas' ? 'Reglas' : 'Contenido'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {activeView === 'contenido' ? (
          <>
            {/* Tabs */}
            <div className="flex items-center justify-center mb-6">
              <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeTab === tab.id
                        ? 'bg-primary-500 text-white shadow-sm'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    <i className={`${tab.icon} text-[10px]`}></i>
                    {tab.label}
                    <span className="text-[9px] opacity-70">({tab.count})</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center justify-center gap-6 mb-8">
              {/* Distance */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Distancia</span>
                <div className="flex gap-1">
                  {opcionesDistancia.slice(0, 5).map(d => (
                    <button
                      key={d.id}
                      onClick={() => setDistanciaFilter(d.id)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                        distanciaFilter === d.id
                          ? 'bg-emerald-500 text-white border-emerald-500'
                          : 'bg-background-100 text-foreground-500 border-background-200 hover:border-emerald-300'
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Language */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Idioma</span>
                <div className="flex gap-1">
                  {opcionesIdioma.map(i => (
                    <button
                      key={i.id}
                      onClick={() => setIdiomaFilter(i.id)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                        idiomaFilter === i.id
                          ? 'bg-amber-500 text-white border-amber-500'
                          : 'bg-background-100 text-foreground-500 border-background-200 hover:border-amber-300'
                      }`}
                    >
                      {i.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category (only for radios) */}
              {activeTab === 'radios' && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Categoría</span>
                  <div className="flex gap-1 flex-wrap justify-center">
                    {opcionesCategoria.slice(0, 7).map(c => (
                      <button
                        key={c.id}
                        onClick={() => setCategoriaFilter(c.id)}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                          categoriaFilter === c.id
                            ? 'bg-primary-500 text-white border-primary-500'
                            : 'bg-background-100 text-foreground-500 border-background-200 hover:border-primary-300'
                        }`}
                      >
                        <i className={`${c.icono} text-[8px]`}></i>
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Radios Grid */}
            {activeTab === 'radios' && (
              radiosFiltradas.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {radiosFiltradas.map(radio => (
                    <RadioCercanaCardFull key={radio.id} radio={radio} onClick={() => setSelectedRadio(radio)} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 text-foreground-400">
                  <i className="ri-radio-line text-4xl mb-3 block"></i>
                  <p className="text-sm font-medium">No se encontraron radios con estos filtros</p>
                  <button onClick={() => { setDistanciaFilter('todas'); setIdiomaFilter('todos'); setCategoriaFilter('todas'); }} className="mt-2 px-4 py-2 rounded-full bg-primary-500 text-white text-xs font-semibold cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap">
                    Limpiar Filtros
                  </button>
                </div>
              )
            )}

            {/* Eventos Grid */}
            {activeTab === 'eventos' && (
              eventosFiltrados.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {eventosFiltrados.map(evento => (
                    <EventoCercanoCardFull key={evento.id} evento={evento} onClick={() => setSelectedEvento(evento)} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 text-foreground-400">
                  <i className="ri-calendar-event-line text-4xl mb-3 block"></i>
                  <p className="text-sm font-medium">No se encontraron eventos con estos filtros</p>
                  <button onClick={() => setDistanciaFilter('todas')} className="mt-2 px-4 py-2 rounded-full bg-primary-500 text-white text-xs font-semibold cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap">
                    Limpiar Filtros
                  </button>
                </div>
              )
            )}

            {/* Locutores Grid */}
            {activeTab === 'locutores' && (
              locutoresFiltrados.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {locutoresFiltrados.map(locutor => (
                    <LocutorCercanoCardFull key={locutor.id} locutor={locutor} onClick={() => navigate('/sistema-locutores')} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 text-foreground-400">
                  <i className="ri-user-voice-line text-4xl mb-3 block"></i>
                  <p className="text-sm font-medium">No se encontraron locutores con estos filtros</p>
                  <button onClick={() => { setDistanciaFilter('todas'); setIdiomaFilter('todos'); }} className="mt-2 px-4 py-2 rounded-full bg-primary-500 text-white text-xs font-semibold cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap">
                    Limpiar Filtros
                  </button>
                </div>
              )
            )}

            {/* CTA + Regla */}
            <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/50 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-user-line text-xl text-emerald-600"></i>
              </div>
              <p className="text-base font-bold text-foreground-950 mb-2">Siempre permitir cambiar ubicación manualmente.</p>
              <p className="text-xs text-foreground-600 max-w-lg mx-auto mb-5">
                Podés buscar radios en cualquier ciudad del mundo con un solo clic. La geolocalización automática es una sugerencia, nunca una imposición.
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Link to="/explorar" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap">
                  <i className="ri-compass-line"></i>Explorar Más Radios
                </Link>
                <Link to="/tv-app" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-950 text-sm font-semibold cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap border border-background-200">
                  <i className="ri-tv-line"></i>Ver en TV App
                </Link>
              </div>
            </div>
          </>
        ) : (
          /* Reglas View */
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-foreground-950 text-center">Reglas del Sistema</h2>
            <p className="text-sm text-foreground-500 text-center max-w-xl mx-auto">
              Las reglas que gobiernan el descubrimiento local en RFW. Basado en distancia real, siempre con control manual del oyente.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
              {reglasRadiosCercanas.map((regla) => (
                <div key={regla.id} className={`p-6 rounded-2xl bg-gradient-to-br ${regla.gradiente} bg-opacity-10 border bg-background-100`}>
                  <div className="w-12 h-12 rounded-full bg-background-50 flex items-center justify-center mb-4">
                    <i className={`${regla.icono} text-xl text-foreground-950`}></i>
                  </div>
                  <p className="text-base font-bold text-foreground-950 mb-2">{regla.titulo}</p>
                  <p className="text-xs text-foreground-600 leading-relaxed">{regla.descripcion}</p>
                </div>
              ))}
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 max-w-4xl mx-auto mt-8">
              {[
                { label: 'Radios', value: statsRadiosCercanas.totalRadios.toString(), icon: 'ri-radio-line', color: 'text-emerald-600 bg-emerald-50' },
                { label: 'Eventos', value: statsRadiosCercanas.totalEventos.toString(), icon: 'ri-calendar-event-line', color: 'text-violet-600 bg-violet-50' },
                { label: 'Locutores', value: statsRadiosCercanas.totalLocutores.toString(), icon: 'ri-user-voice-line', color: 'text-amber-600 bg-amber-50' },
                { label: 'Ubicaciones', value: statsRadiosCercanas.ubicacionesCubiertas.toString(), icon: 'ri-global-line', color: 'text-sky-600 bg-sky-50' },
                { label: 'Más Cercana', value: '0.5 km', icon: 'ri-map-pin-line', color: 'text-rose-600 bg-rose-50' },
              ].map((s, i) => (
                <div key={i} className="p-4 rounded-xl bg-background-100 border border-background-200/70 text-center">
                  <div className={`w-10 h-10 rounded-full ${s.color} flex items-center justify-center mx-auto mb-2`}>
                    <i className={`${s.icon} text-sm`}></i>
                  </div>
                  <p className="text-base font-bold text-foreground-950">{s.value}</p>
                  <p className="text-[10px] text-foreground-500">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedRadio && <RadioDetailModal radio={selectedRadio} onClose={() => setSelectedRadio(null)} />}
      {selectedEvento && <EventoDetailModal evento={selectedEvento} onClose={() => setSelectedEvento(null)} />}

      {/* Spacer for global player */}
      <div className="hidden md:block h-16"></div>
    </div>
  );
}